import { createAsyncThunk, createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';

import * as api from '../services/api';
import { sleep, testUrl } from '../services/utils';
import { RootState } from '../store';

const generalAdapter = createEntityAdapter();

interface GeneralState {
    reportBugLoading: boolean;
    reportBugError: boolean;
    reportBugSuccess: boolean;
    feedbackLoading: boolean;
    feedbackError: boolean;
    feedbackSuccess: boolean;
    extensionDownloadUrl: string | null;
    extensionDownloadLoading: boolean;
    extensionDownloadError: boolean;
}

const initialState = generalAdapter.getInitialState({
    reportBugLoading: false,
    reportBugError: false,
    reportBugSuccess: false,
    feedbackLoading: false,
    feedbackError: false,
    feedbackSuccess: false,
    extensionDownloadUrl: null,
    extensionDownloadLoading: false,
    extensionDownloadError: false
} as GeneralState);

export const sendBugReport = createAsyncThunk(
    'general/sendBugReport',
    async (options: { workId?: number, details: string, attachmentList?: number[] }) => {
        return await api.sendBugReport(options.details, options.workId, options.attachmentList);
    }
);

export const sendGlobalFeedback = createAsyncThunk(
    'general/sendGlobalFeedback',
    async (options: { details: string, attachmentList?: number[] }) => {
        return await api.sendGlobalFeedback(options.details, options.attachmentList);
    }
);

export const waitForUrl = createAsyncThunk(
    'general/waitForUrl',
    async (url: string) => {
        let success = false;
        let attempts = 10;

        while (!success && attempts > 0) {
            attempts--;

            try {
                await testUrl(url);
                success = true;
            } catch {
                await sleep(3000);
            }
        }

        return Promise.resolve(success);
    }
);

export const fetchExtensionDownloadUrl = createAsyncThunk(
    'general/fetchExtensionDownloadUrl',
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.getExtensionDownloadUrl();
            return response.url;
        } catch (error) {
            return rejectWithValue(error);
        }
    });

const generalSlice = createSlice({
    name: 'general',
    initialState,
    reducers: {
        clearReportBugError(state) {
            state.reportBugError = false;
        },
        clearReportBugSuccess(state) {
            state.reportBugSuccess = false;
        },
        clearFeedbackError(state) {
            state.feedbackError = false;
        },
        clearFeedbackSuccess(state) {
            state.feedbackSuccess = false;
        },
        clearExtensionDownloadError(state) {
            state.extensionDownloadError = false;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(sendBugReport.pending, (state) => {
            state.reportBugLoading = true;
            state.reportBugError = false;
            state.reportBugSuccess = false;
        });
        builder.addCase(sendBugReport.fulfilled, (state) => {
            state.reportBugLoading = false;
            state.reportBugSuccess = true;
        });
        builder.addCase(sendBugReport.rejected, (state) => {
            state.reportBugLoading = false;
            state.reportBugError = true;
        });
        builder.addCase(sendGlobalFeedback.pending, (state) => {
            state.feedbackLoading = true;
            state.feedbackError = false;
            state.feedbackSuccess = false;
        });
        builder.addCase(sendGlobalFeedback.fulfilled, (state) => {
            state.feedbackLoading = false;
            state.feedbackSuccess = true;
        });
        builder.addCase(sendGlobalFeedback.rejected, (state) => {
            state.feedbackLoading = false;
            state.feedbackError = true;
        });
        builder.addCase(fetchExtensionDownloadUrl.pending, (state) => {
            state.extensionDownloadLoading = true;
            state.extensionDownloadError = false;
        });
        builder.addCase(fetchExtensionDownloadUrl.fulfilled, (state, action) => {
            state.extensionDownloadLoading = false;
            state.extensionDownloadUrl = action.payload;
        });
        builder.addCase(fetchExtensionDownloadUrl.rejected, (state) => {
            state.extensionDownloadLoading = false;
            state.extensionDownloadError = true;
        });
    }
});

export const { clearReportBugError, clearReportBugSuccess, clearFeedbackError, clearFeedbackSuccess, clearExtensionDownloadError } = generalSlice.actions;

export default generalSlice.reducer;

interface GeneralSelectorsType {
    isReportBugLoading: boolean;
    isReportBugError: boolean;
    isReportBugSuccess: boolean;
    isFeedbackLoading: boolean;
    isFeedbackError: boolean;
    isFeedbackSuccess: boolean;
    extensionDownloadUrl: string | null;
    isExtensionDownloadLoading: boolean;
    isExtensionDownloadError: boolean;
}

export const GeneralSelectors = (): GeneralSelectorsType => {
    const isReportBugLoading = useSelector(
        (state: RootState) => state.general.reportBugLoading
    );

    const isReportBugError = useSelector(
        (state: RootState) => state.general.reportBugError
    );

    const isReportBugSuccess = useSelector(
        (state: RootState) => state.general.reportBugSuccess
    );

    const isFeedbackLoading = useSelector(
        (state: RootState) => state.general.feedbackLoading
    );

    const isFeedbackError = useSelector(
        (state: RootState) => state.general.feedbackError
    );

    const isFeedbackSuccess = useSelector(
        (state: RootState) => state.general.feedbackSuccess
    );

    const extensionDownloadUrl = useSelector(
        (state: RootState) => state.general.extensionDownloadUrl
    );

    const isExtensionDownloadLoading = useSelector(
        (state: RootState) => state.general.extensionDownloadLoading
    );

    const isExtensionDownloadError = useSelector(
        (state: RootState) => state.general.extensionDownloadError
    );

    return {
        isReportBugLoading,
        isReportBugError,
        isReportBugSuccess,
        isFeedbackLoading,
        isFeedbackError,
        isFeedbackSuccess,
        extensionDownloadUrl,
        isExtensionDownloadLoading,
        isExtensionDownloadError
    };
};
